import type { BuilderNode } from "@/shared/builder-session";

const DISPLAY_NAMES: Record<string, string> = {
  salesforce: "Salesforce",
  quickbooks: "QuickBooks",
  netsuite: "NetSuite",
  hubspot: "HubSpot",
  gmail: "Gmail",
  slack: "Slack",
  zendesk: "Zendesk",
  jira: "Jira",
  asana: "Asana",
  notion: "Notion",
  github: "GitHub",
  google: "Google",
  googlesheets: "Google Sheets",
  googledrive: "Google Drive",
  outlook: "Outlook",
  stripe: "Stripe",
  shopify: "Shopify",
  airtable: "Airtable",
  servicenow: "ServiceNow",
};

const IGNORE = new Set([
  "mcp",
  "tool",
  "code",
  "javascript",
  "condition",
  "loop",
  "end",
  "trigger",
  "action",
  "workflow",
  "node",
  "step",
  "default",
]);

function formatToolkit(raw: string): string {
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "");
  if (!key || IGNORE.has(key)) return "";
  if (DISPLAY_NAMES[key]) return DISPLAY_NAMES[key];
  const label = raw.replaceAll("_", " ").trim();
  if (!label) return "";
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

function toolkitFromSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed) return "";
  if (trimmed.includes("_") && /^[A-Za-z][A-Za-z0-9]*_/.test(trimmed)) {
    return formatToolkit(trimmed.split("_")[0] ?? "");
  }
  return formatToolkit(trimmed);
}

function addToolkit(value: unknown, into: Set<string>) {
  if (typeof value !== "string") return;
  const label = toolkitFromSlug(value);
  if (label) into.add(label);
}

function walkConfig(config: unknown, into: Set<string>) {
  if (!config || typeof config !== "object") return;
  const record = config as Record<string, unknown>;

  const toolCall = record.toolCall;
  if (toolCall && typeof toolCall === "object") {
    const call = toolCall as Record<string, unknown>;
    addToolkit(call.toolName, into);
    addToolkit(call.toolSlug, into);
    addToolkit(call.toolkit, into);
  }

  const integration = record.integration;
  if (integration && typeof integration === "object") {
    const integ = integration as Record<string, unknown>;
    addToolkit(integ.system, into);
    addToolkit(integ.toolkit, into);
    addToolkit(integ.app, into);
  }

  addToolkit(record.toolName, into);
  addToolkit(record.toolSlug, into);
  addToolkit(record.toolkit, into);
  addToolkit(record.system, into);
}

/** Systems/toolkits referenced by this workflow graph (v2 connections-used style). */
export function systemsFromBuilderNodes(nodes: BuilderNode[]): string[] {
  const into = new Set<string>();
  for (const node of nodes) {
    walkConfig(node.data?.config, into);
    walkConfig(node.data, into);
    const integration = node.data?.integration;
    if (integration && typeof integration === "object") {
      addToolkit((integration as Record<string, unknown>).system, into);
    }
  }
  return [...into].sort((a, b) => a.localeCompare(b));
}

export function normalizeSystemKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

/** Composio CDN logo (or provided logo URL) for a toolkit / system label. */
export function toolkitLogoSrc(value: string, logo?: string | null) {
  if (logo) return logo;
  const slug = normalizeSystemKey(value) || "composio";
  return `https://logos.composio.dev/api/${slug}`;
}

export function connectionMatchesSystems(
  connection: { slug?: string; name?: string },
  systems: string[],
): boolean {
  if (systems.length === 0) return false;
  const keys = systems.map(normalizeSystemKey);
  const candidates = [connection.slug, connection.name]
    .filter(Boolean)
    .map((value) => normalizeSystemKey(String(value)));
  // Also match toolkit prefix from COMPOSIO_SLUG style names
  for (const candidate of [...candidates]) {
    const prefix = candidate.split(/[^a-z0-9]/)[0];
    if (prefix) candidates.push(prefix);
  }
  return candidates.some((candidate) =>
    keys.some((key) => candidate === key || candidate.startsWith(key) || key.startsWith(candidate)),
  );
}
